import { Map, Marker } from "@vis.gl/react-google-maps";
import { FC, useEffect, useRef, useState } from "react";

type MapViewProps = {
  placeLocation: { lat: number; lng: number } | null;
  geocodedLocation: { lat: number; lng: number } | null;
  placeDetails: google.maps.places.PlaceResult | null;
  geoCoderDetails: google.maps.GeocoderResult | null;
  onMarkerClick: (
    type: "place" | "geocoder",
    place: google.maps.places.PlaceResult | google.maps.GeocoderResult
  ) => void;
};

const areLocationsClose = (
  loc1: { lat: number; lng: number },
  loc2: { lat: number; lng: number },
  threshold = 0.0003
) => {
  const distance = Math.sqrt(Math.pow(loc1.lat - loc2.lat, 2) + Math.pow(loc1.lng - loc2.lng, 2));
  return distance < threshold;
};

const defaultCenter = { lat: 48.8566, lng: 2.3522 };

const MapView: FC<MapViewProps> = ({
  placeLocation,
  geocodedLocation,
  placeDetails,
  geoCoderDetails,
  onMarkerClick,
}) => {
  const showOneMarker =
    placeLocation && geocodedLocation && areLocationsClose(placeLocation, geocodedLocation);

  const mapRef = useRef<google.maps.Map | null>(null);
  const [isMapReady, setMapReady] = useState(false);
  const hasSetView = useRef(false);

  const centerMap = (
    map: google.maps.Map,
    placeLocation: { lat: number; lng: number } | null,
    geocodedLocation: { lat: number; lng: number } | null,
    showOneMarker: boolean | null
  ) => {
    if (!placeLocation && !geocodedLocation) {
      map.setCenter(defaultCenter);
    } else if (placeLocation && geocodedLocation && !showOneMarker) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(placeLocation);
      bounds.extend(geocodedLocation);
      map.fitBounds(bounds);
    } else if (placeLocation) {
      map.setCenter(placeLocation);
    } else if (geocodedLocation) {
      map.setCenter(geocodedLocation);
    }
  };

  useEffect(() => {
    if (!mapRef.current || hasSetView.current) return;
    centerMap(mapRef.current, placeLocation, geocodedLocation, showOneMarker);
    hasSetView.current = true;
  }, [placeLocation, geocodedLocation, showOneMarker, isMapReady]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (mapRef.current) {
        google.maps.event.trigger(mapRef.current, "resize");
        setTimeout(() => {
          centerMap(mapRef.current!, placeLocation, geocodedLocation, showOneMarker);
        }, 300);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [placeLocation, geocodedLocation, showOneMarker]);

  return (
    <Map
      id={"map"}
      defaultCenter={defaultCenter}
      defaultZoom={15}
      gestureHandling="greedy"
      disableDefaultUI={false}
      onIdle={(event) => {
        mapRef.current = event.map;
        setMapReady(true);
      }}
      streetViewControl={false}
      fullscreenControl={false}
    >
      {showOneMarker && placeLocation ? (
        <Marker
          position={placeLocation}
          onClick={() => placeDetails && onMarkerClick("place", placeDetails)}
          icon="http://maps.google.com/mapfiles/ms/icons/blue-dot.png"
        />
      ) : (
        <>
          {placeLocation && (
            <Marker
              position={placeLocation}
              onClick={() => placeDetails && onMarkerClick("place", placeDetails)}
              icon="http://maps.google.com/mapfiles/ms/icons/blue-dot.png"
            />
          )}
          {geocodedLocation && (
            <Marker
              position={geocodedLocation}
              onClick={() => geoCoderDetails && onMarkerClick("geocoder", geoCoderDetails!)}
              icon="http://maps.google.com/mapfiles/ms/icons/red-dot.png"
            />
          )}
        </>
      )}
    </Map>
  );
};

export default MapView;
